/**
 * ONE page design, and a choice of colour (owner, 2026-10-01: *"keep only one design. Fix format and
 * only give colour options… We do not need multiple layout. Keep one simplest layout that can be
 * configured neat and clean way."*).
 *
 * ⚠ ONE FILE FOR BOTH SITES (review MR !1 S27 / W38). The partner portal's editor canvas and the patient site's
 * published page used to keep a copy each, with a comment saying they could not share a file. They can: both
 * projects map `@lm-shared` here. The colour classes therefore cannot disagree - a partner who picks a colour in the
 * editor opens the same one on the patient site.
 *
 * ⚠ THE THREE OTHER DESIGNS ARE GONE FROM THE CODE, NOT HIDDEN. A page saved with one of them is
 * drawn with this design — the server already answers `CLINICAL_HERO` for every page — so nothing
 * needs migrating and no stored value can reach a class attribute.
 *
 * Plain types of its own: this folder imports Angular only (see `../index.ts`), so it cannot name the portal's
 * `MarketingPageLayoutTemplate` / `MarketingThemeAccent`; the unions below are the same strings and are assignable both ways.
 */
export type PageLayoutTemplate = 'CLINICAL_HERO';

export type PageThemeAccent = 'TEAL' | 'BLUE' | 'GREEN' | 'PURPLE' | 'MAROON' | 'ORANGE' | 'AQUA';

export const DEFAULT_PAGE_LAYOUT_TEMPLATE: PageLayoutTemplate = 'CLINICAL_HERO';

/** Whatever a page has stored in `theme.layoutTemplate`, the one design draws it - so nothing stored is read. */
export function normalisePageLayoutTemplate(): PageLayoutTemplate {
    return DEFAULT_PAGE_LAYOUT_TEMPLATE;
}

/** The colour a page starts with — the design's own, and the one the server resolves to. */
export const DEFAULT_PAGE_ACCENT: PageThemeAccent = 'TEAL';

export interface PageColourChoice {
    key: PageThemeAccent;
    label: string;
    /** The button colour, shown on the swatch so the choice is visible before it is made. */
    swatch: string;
}

/**
 * The colours a partner picks between.
 *
 * ⚠ EACH IS A WHOLE FAMILY ON THE PAGE, NOT ONE SWATCH — button, hover, tints, hero band and hairlines
 * all move together (see `.pp-accent-*` in both page stylesheets). The swatch is the button colour.
 *
 * ⚠ THE SAME LIST IS ON THE SERVER (`THEME_ACCENT`). A colour here the server does not accept is
 * refused on save; a colour there that is missing here simply cannot be chosen.
 */
export const PAGE_COLOUR_CHOICES: ReadonlyArray<PageColourChoice> = [
    { key: 'TEAL', label: 'Teal', swatch: '#0b8f8a' },
    { key: 'BLUE', label: 'Blue', swatch: '#1f5fbf' },
    { key: 'GREEN', label: 'Green', swatch: '#2e7d32' },
    { key: 'PURPLE', label: 'Purple', swatch: '#6a3fa0' },
    { key: 'MAROON', label: 'Maroon', swatch: '#9c2f4f' },
    { key: 'ORANGE', label: 'Orange', swatch: '#c25a12' },
];

const ACCENT_CLASS: Partial<Record<PageThemeAccent, string>> = {
    BLUE: 'pp-accent-blue',
    GREEN: 'pp-accent-green',
    PURPLE: 'pp-accent-purple',
    MAROON: 'pp-accent-maroon',
    ORANGE: 'pp-accent-orange',
};

/**
 * The class that re-colours a page.
 *
 * ⚠ TEAL HAS NO CLASS ON PURPOSE: it is the design's own palette, so a page that never chose a colour
 * — or holds the retired `AQUA` — is drawn exactly as it always was. An unknown value is teal too, so
 * a stored string can never reach a class attribute.
 */
export function pageAccentClass(accent: unknown): string {
    return ACCENT_CLASS[String(accent || '').toUpperCase() as PageThemeAccent] || '';
}

/** A stored colour the picker can mark — the retired `AQUA` and anything unknown read as teal. */
export function normalisePageAccent(accent: unknown): PageThemeAccent {
    const upper = String(accent || '').toUpperCase();
    const known = PAGE_COLOUR_CHOICES.find((choice) => choice.key === upper);
    return known ? known.key : DEFAULT_PAGE_ACCENT;
}
